import { create } from 'zustand'
import type { Pose, Quat, Vec3 } from './spawn'

export interface ModelInstance {
  instanceId: string
  assetId: string
  position: Vec3
  quaternion: Quat
  scale: number
  physics: boolean
}

interface InstancesState {
  list: ModelInstance[]
  selectedId: string | null
  spawn(assetId: string, pose: Pose): string
  duplicate(instanceId: string): string | undefined
  remove(instanceId: string): void
  setScale(instanceId: string, scale: number): void
  setTransform(instanceId: string, position: Vec3, quaternion: Quat): void
  setPhysics(instanceId: string, physics: boolean): void
  select(instanceId: string | null): void
  clear(): void
}

const DUP_OFFSET = 0.15

export const useInstances = create<InstancesState>((set, get) => ({
  list: [],
  selectedId: null,

  spawn(assetId, pose) {
    const instanceId = crypto.randomUUID()
    set((s) => ({
      list: [
        ...s.list,
        {
          instanceId,
          assetId,
          position: pose.position,
          quaternion: pose.quaternion,
          scale: 1,
          physics: false,
        },
      ],
    }))
    return instanceId
  },

  duplicate(instanceId) {
    const src = get().list.find((i) => i.instanceId === instanceId)
    if (!src) return undefined
    const id = crypto.randomUUID()
    set((s) => ({
      list: [
        ...s.list,
        {
          ...src,
          instanceId: id,
          position: [src.position[0] + DUP_OFFSET, src.position[1], src.position[2]],
          physics: false,
        },
      ],
    }))
    return id
  },

  remove(instanceId) {
    set((s) => ({
      list: s.list.filter((i) => i.instanceId !== instanceId),
      selectedId: s.selectedId === instanceId ? null : s.selectedId,
    }))
  },

  setScale(instanceId, scale) {
    set((s) => ({
      list: s.list.map((i) => (i.instanceId === instanceId ? { ...i, scale } : i)),
    }))
  },

  setTransform(instanceId, position, quaternion) {
    set((s) => ({
      list: s.list.map((i) => (i.instanceId === instanceId ? { ...i, position, quaternion } : i)),
    }))
  },

  setPhysics(instanceId, physics) {
    set((s) => ({
      list: s.list.map((i) => (i.instanceId === instanceId ? { ...i, physics } : i)),
    }))
  },

  select(instanceId) {
    set({ selectedId: instanceId })
  },

  clear() {
    set({ list: [], selectedId: null })
  },
}))
